import React, { useState, useEffect, useRef } from 'react';
import { 
  Lock, 
  LogOut, 
  Save, 
  Plus, 
  Trash2, 
  Edit2, 
  X,
  Settings,
  Users,
  Calendar,
  FileText,
  TrendingUp,
  Info,
  Mail,
  Check,
  AlertCircle,
  Upload,
  Image,
  Video,
  File,
  Loader2,
  FolderOpen,
  Filter,
  ChevronDown
} from 'lucide-react';
import * as api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get correct URL (handles both Cloudinary and local URLs)
const getFileUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

// Category configuration
const CATEGORIES = {
  activities: {
    label: 'Activities',
    subcategories: [
      { id: 'turkiye', label: 'Mobility to Türkiye' },
      { id: 'sweden', label: 'Mobility to Sweden' },
      { id: 'etwinning', label: 'eTwinning' }
    ]
  },
  resources: {
    label: 'Resources',
    subcategories: []
  },
  impact: {
    label: 'Impact & Dissemination',
    subcategories: []
  }
};

const AdminPanel = ({ setActiveTab }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [activeSection, setActiveSection] = useState('project');
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ show: false, success: true, message: '' });
  const [uploading, setUploading] = useState(false);
  
  // Data states
  const [projectInfo, setProjectInfo] = useState(null);
  const [about, setAbout] = useState(null);
  const [partners, setPartners] = useState([]);
  const [activities, setActivities] = useState([]);
  const [resources, setResources] = useState([]);
  const [dissemination, setDissemination] = useState(null);
  const [contact, setContact] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  
  // File upload states
  const [selectedCategory, setSelectedCategory] = useState('activities');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubcategory, setFilterSubcategory] = useState('');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  
  // Edit modals
  const [editingPartner, setEditingPartner] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  
  // File input refs
  const logoInputRef = useRef(null);
  const resourceFileInputRef = useRef(null);
  const generalFileInputRef = useRef(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      await api.verifyPassword(password);
      setIsAuthenticated(true);
      fetchAllData();
    } catch (err) {
      setAuthError('Invalid password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [proj, ab, part, act, res, diss, cont] = await Promise.all([
        api.getProjectInfo(),
        api.getAbout(),
        api.getPartners(),
        api.getActivities(),
        api.getResources(),
        api.getDissemination(),
        api.getContact()
      ]);
      setProjectInfo(proj);
      setAbout(ab);
      setPartners(part);
      setActivities(act);
      setResources(res);
      setDissemination(diss);
      setContact(cont);
      await fetchUploadedFiles();
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUploadedFiles = async () => {
    try {
      const files = await api.getUploadedFiles(filterCategory || null, filterSubcategory || null);
      setUploadedFiles(files);
    } catch (err) {
      console.error('Error fetching files:', err);
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeSection === 'files') {
      fetchUploadedFiles();
    }
  }, [filterCategory, filterSubcategory, isAuthenticated, activeSection]);

  const showSaveStatus = (success, message) => {
    setSaveStatus({ show: true, success, message });
    setTimeout(() => setSaveStatus({ show: false, success: true, message: '' }), 3000);
  };

  // File upload handler
  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await api.uploadFile(
        file, 
        selectedCategory, 
        selectedSubcategory || null,
        uploadTitle || file.name,
        uploadDescription || null
      );
      const fullUrl = getFileUrl(result.url);
      showSaveStatus(true, `File "${file.name}" uploaded to ${CATEGORIES[selectedCategory].label}!`);
      await fetchUploadedFiles();
      setShowUploadModal(false);
      setUploadTitle('');
      setUploadDescription('');
      return fullUrl;
    } catch (err) {
      showSaveStatus(false, `Failed to upload file: ${err.message}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      try {
        const result = await api.uploadFile(file, 'resources', null, 'Project Logo', 'Main project logo');
        const url = getFileUrl(result.url);
        setProjectInfo({ ...projectInfo, logo: url });
        showSaveStatus(true, 'Logo uploaded successfully!');
      } catch (err) {
        showSaveStatus(false, 'Failed to upload logo');
      } finally {
        setUploading(false);
      }
    }
  };

  // Resource file upload
  const handleResourceFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file && editingResource) {
      setUploading(true);
      try {
        const result = await api.uploadFile(file, 'resources', null, editingResource.title || file.name);
        const fullUrl = getFileUrl(result.url);
        setEditingResource({
          ...editingResource,
          download_url: fullUrl,
          format: result.file_type === 'video' ? 'Video' : 
                  result.file_type === 'document' ? 'PDF' : 
                  result.file_type === 'image' ? 'Image' : 'File'
        });
        showSaveStatus(true, 'File uploaded!');
      } catch (err) {
        showSaveStatus(false, 'Failed to upload file');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleSaveProjectInfo = async () => {
    try {
      await api.updateProjectInfo(projectInfo);
      showSaveStatus(true, 'Project info saved successfully!');
    } catch (err) {
      showSaveStatus(false, 'Error saving project info');
    }
  };

  const handleSaveAbout = async () => {
    try {
      await api.updateAbout(about);
      showSaveStatus(true, 'About section saved successfully!');
    } catch (err) {
      showSaveStatus(false, 'Error saving about section');
    }
  };

  const handleSavePartner = async () => {
    try {
      if (editingPartner.id && editingPartner.id !== 'new') {
        await api.updatePartner(editingPartner.id, editingPartner);
      } else {
        const { id, ...partnerData } = editingPartner;
        await api.createPartner(partnerData);
      }
      await fetchAllData();
      setEditingPartner(null);
      showSaveStatus(true, 'Partner saved successfully!');
    } catch (err) {
      showSaveStatus(false, 'Error saving partner');
    }
  };

  const handleDeletePartner = async (id) => {
    if (window.confirm('Are you sure you want to delete this partner?')) {
      try {
        await api.deletePartner(id);
        await fetchAllData();
        showSaveStatus(true, 'Partner deleted successfully!');
      } catch (err) {
        showSaveStatus(false, 'Error deleting partner');
      }
    }
  };

  const handleSaveActivity = async () => {
    try {
      if (editingActivity.id && editingActivity.id !== 'new') {
        await api.updateActivity(editingActivity.id, editingActivity);
      } else {
        const { id, ...activityData } = editingActivity;
        await api.createActivity(activityData);
      }
      await fetchAllData();
      setEditingActivity(null);
      showSaveStatus(true, 'Activity saved successfully!');
    } catch (err) {
      showSaveStatus(false, 'Error saving activity');
    }
  };

  const handleDeleteActivity = async (id) => {
    if (window.confirm('Are you sure you want to delete this activity?')) {
      try {
        await api.deleteActivity(id);
        await fetchAllData();
        showSaveStatus(true, 'Activity deleted successfully!');
      } catch (err) {
        showSaveStatus(false, 'Error deleting activity');
      }
    }
  };

  const handleSaveResource = async () => {
    try {
      if (editingResource.id && editingResource.id !== 'new') {
        await api.updateResource(editingResource.id, editingResource);
      } else {
        const { id, ...resourceData } = editingResource;
        await api.createResource(resourceData);
      }
      await fetchAllData();
      setEditingResource(null);
      showSaveStatus(true, 'Resource saved successfully!');
    } catch (err) {
      showSaveStatus(false, 'Error saving resource');
    }
  };

  const handleDeleteResource = async (id) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        await api.deleteResource(id);
        await fetchAllData();
        showSaveStatus(true, 'Resource deleted successfully!');
      } catch (err) {
        showSaveStatus(false, 'Error deleting resource');
      }
    }
  };

  const handleSaveDissemination = async () => {
    try {
      await api.updateDissemination(dissemination);
      showSaveStatus(true, 'Impact section saved successfully!');
    } catch (err) {
      showSaveStatus(false, 'Error saving impact section');
    }
  };

  const handleSaveContact = async () => {
    try {
      await api.updateContact(contact);
      showSaveStatus(true, 'Contact info saved successfully!');
    } catch (err) {
      showSaveStatus(false, 'Error saving contact info');
    }
  };

  const handleDeleteUploadedFile = async (fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        await api.deleteUploadedFile(fileId);
        await fetchUploadedFiles();
        showSaveStatus(true, 'File deleted successfully!');
      } catch (err) {
        showSaveStatus(false, 'Error deleting file');
      }
    }
  };

  const handleUpdateFile = async () => {
    if (!editingFile) return;
    try {
      await api.updateFileMetadata(editingFile.id, {
        title: editingFile.title,
        description: editingFile.description,
        category: editingFile.category,
        subcategory: editingFile.subcategory
      });
      await fetchUploadedFiles();
      setEditingFile(null);
      showSaveStatus(true, 'File updated successfully!');
    } catch (err) {
      showSaveStatus(false, 'Error updating file');
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image':
        return <Image className="w-5 h-5" />;
      case 'video':
        return <Video className="w-5 h-5" />;
      case 'document':
        return <FileText className="w-5 h-5" />;
      default:
        return <File className="w-5 h-5" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getCategoryLabel = (category, subcategory) => {
    const cat = CATEGORIES[category];
    if (!cat) return category;
    if (subcategory) {
      const sub = cat.subcategories.find(s => s.id === subcategory);
      return sub ? `${cat.label} → ${sub.label}` : `${cat.label} → ${subcategory}`;
    }
    return cat.label;
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-[calc(100vh-80px)] bg-[#f8fafc] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#1e3a5f]">Admin Panel</h1>
            <p className="text-gray-500 mt-2">Enter password to access</p>
          </div>
          
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30 focus:border-[#1e3a5f]"
                placeholder="Enter admin password"
              />
            </div>
            
            {authError && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {authError}
              </div>
            )}
            
            <button
              type="submit"
              className="w-full py-3 bg-[#1e3a5f] text-white rounded-xl font-medium hover:bg-[#2c5282] transition-colors duration-300"
            >
              Login
            </button>
          </form>
          
          <button
            onClick={() => setActiveTab('home')}
            className="w-full mt-4 py-2 text-gray-500 hover:text-[#1e3a5f] text-sm"
          >
            ← Back to Website
          </button>
        </div>
      </div>
    );
  }

  const sections = [
    { id: 'project', label: 'Project Info', icon: Settings },
    { id: 'about', label: 'About', icon: Info },
    { id: 'partners', label: 'Partners', icon: Users },
    { id: 'activities', label: 'Activities', icon: Calendar },
    { id: 'resources', label: 'Resources', icon: FileText },
    { id: 'impact', label: 'Impact', icon: TrendingUp },
    { id: 'contact', label: 'Contact', icon: Mail },
    { id: 'files', label: 'Media Library', icon: FolderOpen },
  ];

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#f8fafc]">
      {/* Save Status Toast */}
      {saveStatus.show && (
        <div className={`fixed top-24 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg ${
          saveStatus.success ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {saveStatus.success ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {saveStatus.message}
        </div>
      )}

      {/* Upload indicator */}
      {uploading && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg bg-[#1e3a5f] text-white">
          <Loader2 className="w-5 h-5 animate-spin" />
          Uploading file...
        </div>
      )}

      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#1e3a5f]">Admin Panel</h1>
            <p className="text-gray-500">Manage your website content</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveTab('home')}
              className="px-4 py-2 text-[#1e3a5f] hover:bg-[#1e3a5f]/10 rounded-lg transition-colors"
            >
              View Website
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                      activeSection === section.id
                        ? 'bg-[#1e3a5f] text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <section.icon className="w-5 h-5" />
                    {section.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-grow">
            {loading ? (
              <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                <div className="animate-spin w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading...</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                {/* Project Info Section */}
                {activeSection === 'project' && projectInfo && (
                  <div>
                    <h2 className="text-xl font-bold text-[#1e3a5f] mb-6">Project Information</h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                        <input
                          type="text"
                          value={projectInfo.name || ''}
                          onChange={(e) => setProjectInfo({...projectInfo, name: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
                        <input
                          type="text"
                          value={projectInfo.tagline || ''}
                          onChange={(e) => setProjectInfo({...projectInfo, tagline: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Project Logo</label>
                        <div className="flex items-start gap-4">
                          {projectInfo.logo && (
                            <img src={projectInfo.logo} alt="Logo preview" className="w-24 h-24 object-contain border rounded-lg" />
                          )}
                          <div className="flex-grow">
                            <input
                              type="text"
                              value={projectInfo.logo || ''}
                              onChange={(e) => setProjectInfo({...projectInfo, logo: e.target.value})}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30 mb-2"
                              placeholder="Logo URL"
                            />
                            <input
                              type="file"
                              ref={logoInputRef}
                              onChange={handleLogoUpload}
                              accept="image/*"
                              className="hidden"
                            />
                            <button
                              onClick={() => logoInputRef.current?.click()}
                              disabled={uploading}
                              className="flex items-center gap-2 px-4 py-2 bg-[#f0f7ff] text-[#1e3a5f] rounded-lg hover:bg-[#1e3a5f] hover:text-white transition-colors disabled:opacity-50"
                            >
                              <Upload className="w-4 h-4" />
                              Upload New Logo
                            </button>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                        <input
                          type="text"
                          value={projectInfo.duration || ''}
                          onChange={(e) => setProjectInfo({...projectInfo, duration: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Project Number</label>
                        <input
                          type="text"
                          value={projectInfo.project_number || ''}
                          onChange={(e) => setProjectInfo({...projectInfo, project_number: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      <button
                        onClick={handleSaveProjectInfo}
                        className="flex items-center gap-2 px-6 py-2 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                    </div>
                  </div>
                )}

                {/* About Section */}
                {activeSection === 'about' && about && (
                  <div>
                    <h2 className="text-xl font-bold text-[#1e3a5f] mb-6">About Section</h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Introduction</label>
                        <textarea
                          value={about.introduction || ''}
                          onChange={(e) => setAbout({...about, introduction: e.target.value})}
                          rows={4}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Objectives (one per line)</label>
                        <textarea
                          value={(about.objectives || []).join('\n')}
                          onChange={(e) => setAbout({...about, objectives: e.target.value.split('\n').filter(x => x.trim())})}
                          rows={5}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Target Groups (one per line)</label>
                        <textarea
                          value={(about.target_groups || []).join('\n')}
                          onChange={(e) => setAbout({...about, target_groups: e.target.value.split('\n').filter(x => x.trim())})}
                          rows={4}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Expected Results (one per line)</label>
                        <textarea
                          value={(about.expected_results || []).join('\n')}
                          onChange={(e) => setAbout({...about, expected_results: e.target.value.split('\n').filter(x => x.trim())})}
                          rows={5}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      <button
                        onClick={handleSaveAbout}
                        className="flex items-center gap-2 px-6 py-2 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                    </div>
                  </div>
                )}

                {/* Partners Section */}
                {activeSection === 'partners' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-[#1e3a5f]">Partners</h2>
                      <button
                        onClick={() => setEditingPartner({ id: 'new', name: '', country: '', flag: '', role: 'Partner', description: '', website: '', city: '' })}
                        className="flex items-center gap-2 px-4 py-2 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add Partner
                      </button>
                    </div>
                    <div className="space-y-4">
                      {partners.map((partner) => (
                        <div key={partner.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{partner.flag}</span>
                            <div>
                              <p className="font-medium text-[#1e3a5f]">{partner.name}</p>
                              <p className="text-sm text-gray-500">{partner.city}, {partner.country}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingPartner(partner)}
                              className="p-2 text-gray-500 hover:text-[#1e3a5f] hover:bg-white rounded-lg transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeletePartner(partner.id)}
                              className="p-2 text-gray-500 hover:text-red-500 hover:bg-white rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Activities Section */}
                {activeSection === 'activities' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-[#1e3a5f]">Activities</h2>
                      <button
                        onClick={() => setEditingActivity({ id: 'new', title: '', type: 'mobility', date: '', location: '', description: '', participants: '', highlights: [] })}
                        className="flex items-center gap-2 px-4 py-2 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add Activity
                      </button>
                    </div>
                    <div className="space-y-4">
                      {activities.map((activity) => (
                        <div key={activity.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                          <div>
                            <p className="font-medium text-[#1e3a5f]">{activity.title}</p>
                            <p className="text-sm text-gray-500">{activity.date} • {activity.location}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingActivity(activity)}
                              className="p-2 text-gray-500 hover:text-[#1e3a5f] hover:bg-white rounded-lg transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteActivity(activity.id)}
                              className="p-2 text-gray-500 hover:text-red-500 hover:bg-white rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resources Section */}
                {activeSection === 'resources' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-[#1e3a5f]">Resources</h2>
                      <button
                        onClick={() => setEditingResource({ id: 'new', title: '', type: 'lesson-plan', format: 'PDF', description: '', download_url: '', language: 'English' })}
                        className="flex items-center gap-2 px-4 py-2 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add Resource
                      </button>
                    </div>
                    <div className="space-y-4">
                      {resources.map((resource) => (
                        <div key={resource.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#1e3a5f] rounded-lg flex items-center justify-center text-white">
                              {resource.type === 'video' ? <Video className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                            </div>
                            <div>
                              <p className="font-medium text-[#1e3a5f]">{resource.title}</p>
                              <p className="text-sm text-gray-500">{resource.format} • {resource.language}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingResource(resource)}
                              className="p-2 text-gray-500 hover:text-[#1e3a5f] hover:bg-white rounded-lg transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteResource(resource.id)}
                              className="p-2 text-gray-500 hover:text-red-500 hover:bg-white rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Impact Section */}
                {activeSection === 'impact' && dissemination && (
                  <div>
                    <h2 className="text-xl font-bold text-[#1e3a5f] mb-6">Impact & Dissemination</h2>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Introduction</label>
                        <textarea
                          value={dissemination.introduction || ''}
                          onChange={(e) => setDissemination({...dissemination, introduction: e.target.value})}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Impact Metrics</label>
                        {(dissemination.impact || []).map((item, index) => (
                          <div key={index} className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={item.value}
                              onChange={(e) => {
                                const newImpact = [...dissemination.impact];
                                newImpact[index].value = e.target.value;
                                setDissemination({...dissemination, impact: newImpact});
                              }}
                              placeholder="Value"
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg"
                            />
                            <input
                              type="text"
                              value={item.metric}
                              onChange={(e) => {
                                const newImpact = [...dissemination.impact];
                                newImpact[index].metric = e.target.value;
                                setDissemination({...dissemination, impact: newImpact});
                              }}
                              placeholder="Metric name"
                              className="flex-grow px-3 py-2 border border-gray-200 rounded-lg"
                            />
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => {
                                const newImpact = [...dissemination.impact];
                                newImpact[index].description = e.target.value;
                                setDissemination({...dissemination, impact: newImpact});
                              }}
                              placeholder="Description"
                              className="flex-grow px-3 py-2 border border-gray-200 rounded-lg"
                            />
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={handleSaveDissemination}
                        className="flex items-center gap-2 px-6 py-2 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                    </div>
                  </div>
                )}

                {/* Contact Section */}
                {activeSection === 'contact' && contact && (
                  <div>
                    <h2 className="text-xl font-bold text-[#1e3a5f] mb-6">Contact Information</h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={contact.email || ''}
                          onChange={(e) => setContact({...contact, email: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Coordinator</label>
                        <input
                          type="text"
                          value={contact.coordinator || ''}
                          onChange={(e) => setContact({...contact, coordinator: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                        <input
                          type="text"
                          value={contact.country || ''}
                          onChange={(e) => setContact({...contact, country: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                        />
                      </div>
                      <button
                        onClick={handleSaveContact}
                        className="flex items-center gap-2 px-6 py-2 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                    </div>
                  </div>
                )}

                {/* Media Library Section */}
                {activeSection === 'files' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-[#1e3a5f]">Media Library</h2>
                      <button
                        onClick={() => setShowUploadModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        Upload File
                      </button>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Filter:</span>
                      </div>
                      <select
                        value={filterCategory}
                        onChange={(e) => {
                          setFilterCategory(e.target.value);
                          setFilterSubcategory('');
                        }}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      >
                        <option value="">All Categories</option>
                        {Object.entries(CATEGORIES).map(([key, cat]) => (
                          <option key={key} value={key}>{cat.label}</option>
                        ))}
                      </select>
                      {filterCategory === 'activities' && (
                        <select
                          value={filterSubcategory}
                          onChange={(e) => setFilterSubcategory(e.target.value)}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        >
                          <option value="">All Activities</option>
                          {CATEGORIES.activities.subcategories.map(sub => (
                            <option key={sub.id} value={sub.id}>{sub.label}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* File Grid */}
                    {uploadedFiles.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-xl">
                        <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No files uploaded yet</p>
                        <p className="text-sm text-gray-400 mt-1">Upload photos, documents, and videos</p>
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {uploadedFiles.map((file) => (
                          <div key={file.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                            <div className="flex items-center gap-4 flex-grow min-w-0">
                              {/* Preview */}
                              {file.file_type === 'image' ? (
                                <img 
                                  src={getFileUrl(file.url)} 
                                  alt={file.title}
                                  className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                />
                              ) : (
                                <div className={`w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  file.file_type === 'video' ? 'bg-purple-100 text-purple-600' :
                                  file.file_type === 'document' ? 'bg-blue-100 text-blue-600' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {getFileIcon(file.file_type)}
                                </div>
                              )}
                              
                              {/* Info */}
                              <div className="min-w-0 flex-grow">
                                <p className="font-medium text-[#1e3a5f] truncate">{file.title || file.original_name}</p>
                                <p className="text-sm text-gray-500">
                                  {getCategoryLabel(file.category, file.subcategory)} • {formatFileSize(file.size)}
                                </p>
                                {file.description && (
                                  <p className="text-xs text-gray-400 truncate">{file.description}</p>
                                )}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                              <button
                                onClick={() => {
                                  const url = getFileUrl(file.url);
                                  navigator.clipboard.writeText(url);
                                  showSaveStatus(true, 'URL copied!');
                                }}
                                className="px-3 py-1.5 text-sm text-[#1e3a5f] hover:bg-white rounded-lg transition-colors"
                              >
                                Copy URL
                              </button>
                              <a
                                href={getFileUrl(file.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1.5 text-sm text-[#1e3a5f] hover:bg-white rounded-lg transition-colors"
                              >
                                View
                              </a>
                              <button
                                onClick={() => setEditingFile(file)}
                                className="p-2 text-gray-500 hover:text-[#1e3a5f] hover:bg-white rounded-lg transition-colors"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteUploadedFile(file.id)}
                                className="p-2 text-gray-500 hover:text-red-500 hover:bg-white rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#1e3a5f]">Upload File</h3>
              <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setSelectedSubcategory('');
                  }}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                >
                  {Object.entries(CATEGORIES).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              {selectedCategory === 'activities' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Activity *</label>
                  <select
                    value={selectedSubcategory}
                    onChange={(e) => setSelectedSubcategory(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  >
                    <option value="">Select activity...</option>
                    {CATEGORIES.activities.subcategories.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.label}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Enter a title for this file"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">File</label>
                <input
                  type="file"
                  ref={generalFileInputRef}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleFileUpload(file);
                    }
                  }}
                  className="hidden"
                />
                <button
                  onClick={() => generalFileInputRef.current?.click()}
                  disabled={uploading || (selectedCategory === 'activities' && !selectedSubcategory)}
                  className="w-full py-8 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#1e3a5f] hover:bg-[#f0f7ff] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Click to select a file</p>
                  <p className="text-xs text-gray-400 mt-1">Images, PDFs, Documents, Videos</p>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit File Modal */}
      {editingFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#1e3a5f]">Edit File Details</h3>
              <button onClick={() => setEditingFile(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editingFile.title || ''}
                  onChange={(e) => setEditingFile({...editingFile, title: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingFile.description || ''}
                  onChange={(e) => setEditingFile({...editingFile, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={editingFile.category || ''}
                  onChange={(e) => setEditingFile({...editingFile, category: e.target.value, subcategory: ''})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                >
                  {Object.entries(CATEGORIES).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              {editingFile.category === 'activities' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Activity</label>
                  <select
                    value={editingFile.subcategory || ''}
                    onChange={(e) => setEditingFile({...editingFile, subcategory: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  >
                    <option value="">Select activity...</option>
                    {CATEGORIES.activities.subcategories.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.label}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <button
                onClick={handleUpdateFile}
                className="w-full py-3 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Partner Modal */}
      {editingPartner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#1e3a5f]">
                {editingPartner.id === 'new' ? 'Add Partner' : 'Edit Partner'}
              </h3>
              <button onClick={() => setEditingPartner(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={editingPartner.name} onChange={(e) => setEditingPartner({...editingPartner, name: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input type="text" value={editingPartner.country} onChange={(e) => setEditingPartner({...editingPartner, country: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Flag Emoji</label>
                  <input type="text" value={editingPartner.flag} onChange={(e) => setEditingPartner({...editingPartner, flag: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg" placeholder="🇸🇪" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input type="text" value={editingPartner.city} onChange={(e) => setEditingPartner({...editingPartner, city: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select value={editingPartner.role} onChange={(e) => setEditingPartner({...editingPartner, role: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg">
                    <option value="Coordinator">Coordinator</option>
                    <option value="Partner">Partner</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={editingPartner.description} onChange={(e) => setEditingPartner({...editingPartner, description: e.target.value})} rows={3} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                <input type="text" value={editingPartner.website} onChange={(e) => setEditingPartner({...editingPartner, website: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
              </div>
              <button onClick={handleSavePartner} className="w-full py-3 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors">Save Partner</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Activity Modal */}
      {editingActivity && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#1e3a5f]">{editingActivity.id === 'new' ? 'Add Activity' : 'Edit Activity'}</h3>
              <button onClick={() => setEditingActivity(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" value={editingActivity.title} onChange={(e) => setEditingActivity({...editingActivity, title: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={editingActivity.type} onChange={(e) => setEditingActivity({...editingActivity, type: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg">
                    <option value="mobility">Learning Mobility</option>
                    <option value="online">eTwinning/Online</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input type="text" value={editingActivity.date} onChange={(e) => setEditingActivity({...editingActivity, date: e.target.value})} placeholder="e.g., Spring 2025" className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input type="text" value={editingActivity.location} onChange={(e) => setEditingActivity({...editingActivity, location: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Participants</label>
                <input type="text" value={editingActivity.participants} onChange={(e) => setEditingActivity({...editingActivity, participants: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={editingActivity.description} onChange={(e) => setEditingActivity({...editingActivity, description: e.target.value})} rows={3} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Highlights (one per line)</label>
                <textarea value={(editingActivity.highlights || []).join('\n')} onChange={(e) => setEditingActivity({...editingActivity, highlights: e.target.value.split('\n').filter(x => x.trim())})} rows={4} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
              </div>
              <button onClick={handleSaveActivity} className="w-full py-3 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors">Save Activity</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Resource Modal */}
      {editingResource && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#1e3a5f]">{editingResource.id === 'new' ? 'Add Resource' : 'Edit Resource'}</h3>
              <button onClick={() => setEditingResource(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" value={editingResource.title} onChange={(e) => setEditingResource({...editingResource, title: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={editingResource.type} onChange={(e) => setEditingResource({...editingResource, type: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg">
                    <option value="lesson-plan">Lesson Plan</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                  <select value={editingResource.format} onChange={(e) => setEditingResource({...editingResource, format: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg">
                    <option value="PDF">PDF</option>
                    <option value="Video">Video</option>
                    <option value="Document">Document</option>
                    <option value="Image">Image</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={editingResource.description} onChange={(e) => setEditingResource({...editingResource, description: e.target.value})} rows={3} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">File</label>
                <div className="space-y-2">
                  <input type="text" value={editingResource.download_url} onChange={(e) => setEditingResource({...editingResource, download_url: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg" placeholder="File URL (or upload below)" />
                  <input type="file" ref={resourceFileInputRef} onChange={handleResourceFileUpload} className="hidden" />
                  <button onClick={() => resourceFileInputRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-4 py-2 bg-[#f0f7ff] text-[#1e3a5f] rounded-lg hover:bg-[#1e3a5f] hover:text-white transition-colors w-full justify-center disabled:opacity-50">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                <input type="text" value={editingResource.language} onChange={(e) => setEditingResource({...editingResource, language: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-lg" />
              </div>
              <button onClick={handleSaveResource} className="w-full py-3 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2c5282] transition-colors">Save Resource</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
